import { Box, Collapse, Menu, MenuItem } from '@mui/material';
import {
  PAYMENT_STATUSES,
  PAYMENT_UNITS,
  addPayment,
  paymentAmountSuggested,
  paymentScheduleEnabled,
  paymentStatusOn,
  paymentScheduleTotal,
  paymentsOf,
  removePayment,
  updatePayment,
} from '@timelog/pm-shared';
import type { Payment, PaymentDisplayStatus, PaymentStatus, PaymentUnit } from '@timelog/pm-shared';
import { useState } from 'react';
import type { MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';

import {
  parseAmountInput,
  parseHoursInput,
  parsePctInput,
  useContractEditing,
  useFinancials,
} from './editor';

import { MaterialSymbol } from '@/components/MaterialSymbol';
import { InlineDateCell, InlineTextCell } from '@/components/planning/cells';
import type { CommitIntent } from '@/components/planning/cells';
import { MICRO_LABEL_TRACKING } from '@/components/ui';
import {
  formatAmount,
  formatAmountAbbrev,
  formatHours,
  formatShortDate,
  groupDigitsWhileTyping,
  todayIso,
} from '@/lib';

/**
 * The payment schedule of a FIXED-PRICE contract (BR-31): the action under the
 * total amount, and the table it expands into. Each row is one Payment — a
 * commercial intent to invoice the customer for an amount on a date — with the
 * figures that describe it (quantity, unit, rate, discount).
 *
 * The amount is the PM's number (typed, sticky), and the figures are a helper
 * that PREFILLS it (CALC-26) for as long as it still agrees with them; the
 * transform in `contract-ops.ts` decides, this component only reports. The
 * schedule's total is compared with the contract amount underneath and a
 * disagreement is a warning, never a block — a schedule is drawn up before the
 * contract is final at least as often as after.
 *
 * Offered only where the budget-model flag says so (`paymentSchedule: on`,
 * ADR 0027) — every shipped variant is off, so a session opts in — and then
 * only on a fixed-price contract that has an amount: a T&M contract
 * bills what it registers, and an amount-less contract has nothing to
 * schedule yet. The document keeps its payments across a type change (see
 * `ContractDocumentSchema`), so nothing is lost by the gate.
 */

/** The per-row field walk: Tab and Enter move along it, off either end stops editing. */
const FIELDS = ['date', 'text', 'quantity', 'rate', 'discount', 'amount'] as const;
type Field = (typeof FIELDS)[number];

const editKey = (id: string, field: Field) => `payment:${id}:${field}`;

const HEADER_CELL_SX = {
  fontSize: 10,
  fontWeight: 500,
  letterSpacing: MICRO_LABEL_TRACKING,
  textTransform: 'uppercase',
  color: 'var(--fg-3)',
  whiteSpace: 'nowrap',
} as const;

const MONO_CELL_SX = {
  fontFamily: 'var(--font-family-mono)',
  fontSize: 11,
  color: 'var(--fg-1)',
} as const;

const COLUMN_WIDTHS = {
  date: 92,
  quantity: 64,
  unit: 64,
  rate: 84,
  discount: 56,
  amount: 96,
  status: 84,
};

/**
 * The status pill: open is quiet, overdue is the one that wants attention,
 * invoiced is settled. Overdue is derived from the date, so its label is a
 * reading, not a choice — the menu offers only the two stored states.
 */
const STATUS_STYLES: Record<PaymentDisplayStatus, { color: string; bg: string }> = {
  open: { color: 'var(--fg-2)', bg: 'transparent' },
  overdue: { color: 'var(--color-coral-100)', bg: 'var(--color-coral-10)' },
  invoiced: {
    color: 'var(--color-success-100)',
    bg: 'var(--color-success-10)',
  },
};
/** The text column takes what the fixed columns leave, down to this. */
const TEXT_COLUMN_MIN_PX = 120;

export const PaymentSchedule = () => {
  const { state, dispatch, budgetModel } = useFinancials();
  const { isEditing, startEditing, stopEditing } = useContractEditing();
  const [open, setOpen] = useState(false);
  const [unitAnchor, setUnitAnchor] = useState<{ id: string; element: HTMLElement } | null>(null);
  const [statusAnchor, setStatusAnchor] = useState<{ id: string; element: HTMLElement } | null>(
    null,
  );
  // Overdue is read off the calendar; one reading per mount, like the aside's.
  const [today] = useState(todayIso);
  const { t } = useTranslation('pmFinancials');
  const unitLabel = (unit: PaymentUnit) => t(`payments.Units.${unit}`);
  const statusLabel = (status: PaymentDisplayStatus) => t(`payments.Statuses.${status}`);

  const { revenue, currency } = state.document;
  // The schedule is the list of payments; a contract with none carries no key at all.
  const payments = paymentsOf(state.document);
  // The flag decides who sees the action at all (off in every shipped variant, 30 Sep 2026).
  if (!paymentScheduleEnabled(budgetModel.config)) return null;
  if (state.type !== 'fixed' || revenue <= 0) return null;

  const scheduled = paymentScheduleTotal(state.document);
  const difference = revenue - scheduled;
  const comparison =
    payments.length === 0 ? 'none' : difference === 0 ? 'ok' : difference > 0 ? 'under' : 'over';

  const apply = (op: (document: typeof state.document) => typeof state.document) =>
    dispatch({ type: 'APPLY_OP', op });

  const change = (id: string, patch: Partial<Omit<Payment, 'id'>>) =>
    apply((document) => updatePayment(document, id, patch));

  /** Where editing goes after a commit: along the row, then stop. */
  const advance = (id: string, field: Field, intent: CommitIntent) => {
    const step =
      intent === 'tab-next' || intent === 'enter-next' ? 1 : intent === 'tab-prev' ? -1 : 0;
    const next = step === 0 ? undefined : FIELDS[FIELDS.indexOf(field) + step];
    if (next === undefined) stopEditing();
    else startEditing(editKey(id, next));
  };

  const add = () => {
    const id = globalThis.crypto.randomUUID();
    apply((document) => addPayment(document, { invoiceDate: todayIso() }, id));
    setOpen(true);
    startEditing(editKey(id, 'amount'));
  };

  const amountTitle = (payment: Payment): string =>
    payment.amount === paymentAmountSuggested(payment)
      ? t('payments.SuggestedTitle')
      : t('payments.TypedTitle');

  return (
    <Box sx={{ mt: 1 }}>
      <Box
        component="button"
        type="button"
        data-automation-id="payment-schedule-toggle"
        data-state={open ? 'open' : 'closed'}
        data-balance={comparison}
        title={
          comparison === 'under' || comparison === 'over'
            ? t('payments.MismatchTitle', {
                scheduled: formatAmount(scheduled),
                currency,
                revenue: formatAmount(revenue),
              })
            : undefined
        }
        onClick={() => setOpen((value) => !value)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          px: 1,
          py: 0.5,
          border: 'var(--border-width-thin) solid var(--border-divider)',
          borderRadius: 'var(--border-radius-pill)',
          bgcolor: open ? 'var(--bg-sunken)' : 'transparent',
          fontSize: 11,
          fontFamily: 'var(--font-family)',
          color: open ? 'var(--fg-1)' : 'var(--fg-3)',
          cursor: 'pointer',
          '&:hover': { borderColor: 'var(--border-subtle)' },
        }}
      >
        <MaterialSymbol name={open ? 'expand_less' : 'expand_more'} size={14} />
        {open
          ? t('payments.Hide')
          : payments.length === 0
            ? t('payments.Empty')
            : t('payments.Summary', {
                n: payments.length,
                amount: formatAmountAbbrev(scheduled),
                currency,
              })}
        {(comparison === 'under' || comparison === 'over') && (
          /* A discreet dot: the footer inside says how far off it is. */
          <Box
            component="span"
            data-automation-id="payment-schedule-balance-dot"
            sx={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              bgcolor: 'var(--color-warning-80)',
              flex: 'none',
              ml: 0.25,
            }}
          />
        )}
      </Box>

      <Collapse in={open}>
        <Box
          data-automation-id="payment-schedule"
          sx={{
            mt: 1,
            border: 'var(--border-width-thin) solid var(--border-divider)',
            borderRadius: 'var(--border-radius-medium)',
            overflow: 'hidden',
            // Six fixed columns have a hard floor; scroll the table rather than
            // push the card wider than its panel (same call as the rate card).
            overflowX: 'auto',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 1,
              py: 0.5,
              bgcolor: 'var(--bg-sunken)',
              ...HEADER_CELL_SX,
            }}
          >
            <Box sx={{ width: COLUMN_WIDTHS.date }}>{t('payments.Columns.InvoiceDate')}</Box>
            <Box sx={{ flex: 1, minWidth: TEXT_COLUMN_MIN_PX }}>{t('payments.Columns.Text')}</Box>
            <Box sx={{ width: COLUMN_WIDTHS.quantity, textAlign: 'right' }}>
              {t('payments.Columns.Quantity')}
            </Box>
            <Box sx={{ width: COLUMN_WIDTHS.unit }}>{t('payments.Columns.Unit')}</Box>
            <Box sx={{ width: COLUMN_WIDTHS.rate, textAlign: 'right' }}>
              {t('payments.Columns.Rate')}
            </Box>
            <Box sx={{ width: COLUMN_WIDTHS.discount, textAlign: 'right' }}>
              {t('payments.Columns.Discount')}
            </Box>
            <Box sx={{ width: COLUMN_WIDTHS.amount, textAlign: 'right' }}>
              {t('payments.Columns.Amount')}
            </Box>
            <Box sx={{ width: COLUMN_WIDTHS.status }}>{t('payments.Columns.Status')}</Box>
            <Box sx={{ width: 16 }} />
          </Box>

          {payments.map((payment) => (
            <Box
              key={payment.id}
              data-automation-id="payment-row"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 1,
                py: 0.75,
                borderTop: 'var(--border-width-thin) solid var(--border-divider)',
              }}
            >
              <Box sx={{ width: COLUMN_WIDTHS.date }}>
                <InlineDateCell
                  automationId="payment-date"
                  value={payment.invoiceDate}
                  display={
                    <Box component="span" sx={{ fontSize: 11, color: 'var(--fg-1)' }}>
                      {formatShortDate(payment.invoiceDate)}
                    </Box>
                  }
                  editing={isEditing(editKey(payment.id, 'date'))}
                  onStartEdit={() => startEditing(editKey(payment.id, 'date'))}
                  onCancel={stopEditing}
                  onCommit={(iso, intent) => {
                    // A cleared field keeps the date: a payment without one is not a payment.
                    if (iso !== '') change(payment.id, { invoiceDate: iso });
                    advance(payment.id, 'date', intent);
                  }}
                  inputWidth={COLUMN_WIDTHS.date}
                />
              </Box>
              <Box sx={{ flex: 1, minWidth: TEXT_COLUMN_MIN_PX }}>
                <InlineTextCell
                  automationId="payment-text"
                  value={payment.invoiceText ?? ''}
                  placeholder={t('payments.Columns.Text')}
                  editing={isEditing(editKey(payment.id, 'text'))}
                  onStartEdit={() => startEditing(editKey(payment.id, 'text'))}
                  onCancel={stopEditing}
                  advanceOnEnter
                  onCommit={(raw, intent) => {
                    change(payment.id, { invoiceText: raw.trim() });
                    advance(payment.id, 'text', intent);
                  }}
                  inputWidth="100%"
                  inputSx={{ fontSize: 11 }}
                  display={
                    <Box
                      component="span"
                      sx={{
                        display: 'block',
                        fontSize: 11,
                        color: payment.invoiceText ? 'var(--fg-1)' : 'var(--fg-3)',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {payment.invoiceText ?? t('payments.Columns.Text')}
                    </Box>
                  }
                />
              </Box>
              <Box sx={{ width: COLUMN_WIDTHS.quantity, textAlign: 'right' }}>
                <InlineTextCell
                  automationId="payment-quantity"
                  value={formatHours(payment.quantity)}
                  editing={isEditing(editKey(payment.id, 'quantity'))}
                  onStartEdit={() => startEditing(editKey(payment.id, 'quantity'))}
                  onCancel={stopEditing}
                  advanceOnEnter
                  onCommit={(raw, intent) => {
                    const quantity = parseHoursInput(raw);
                    if (quantity !== null) change(payment.id, { quantity });
                    advance(payment.id, 'quantity', intent);
                  }}
                  align="right"
                  inputWidth={COLUMN_WIDTHS.quantity}
                  inputSx={MONO_CELL_SX}
                  display={
                    <Box component="span" sx={MONO_CELL_SX}>
                      {formatHours(payment.quantity)}
                    </Box>
                  }
                />
              </Box>
              <Box sx={{ width: COLUMN_WIDTHS.unit }}>
                <Box
                  component="button"
                  type="button"
                  data-automation-id="payment-unit"
                  onClick={(event: MouseEvent<HTMLElement>) =>
                    setUnitAnchor({ id: payment.id, element: event.currentTarget })
                  }
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.25,
                    px: 0.75,
                    py: 0.25,
                    border: 'var(--border-width-thin) solid var(--border-divider)',
                    borderRadius: 'var(--border-radius-pill)',
                    bgcolor: 'transparent',
                    fontSize: 11,
                    fontFamily: 'var(--font-family)',
                    color: 'var(--fg-2)',
                    cursor: 'pointer',
                    '&:hover': { borderColor: 'var(--border-subtle)', color: 'var(--fg-1)' },
                  }}
                >
                  {unitLabel(payment.unit)}
                  <MaterialSymbol name="expand_more" size={12} />
                </Box>
              </Box>
              <Box sx={{ width: COLUMN_WIDTHS.rate, textAlign: 'right' }}>
                <InlineTextCell
                  automationId="payment-rate"
                  value={formatAmount(payment.rate)}
                  formatWhileTyping={groupDigitsWhileTyping}
                  editing={isEditing(editKey(payment.id, 'rate'))}
                  onStartEdit={() => startEditing(editKey(payment.id, 'rate'))}
                  onCancel={stopEditing}
                  advanceOnEnter
                  onCommit={(raw, intent) => {
                    const rate = parseHoursInput(raw);
                    if (rate !== null) change(payment.id, { rate });
                    advance(payment.id, 'rate', intent);
                  }}
                  align="right"
                  inputWidth={COLUMN_WIDTHS.rate}
                  inputSx={MONO_CELL_SX}
                  display={
                    <Box component="span" sx={MONO_CELL_SX}>
                      {formatAmount(payment.rate)}
                    </Box>
                  }
                />
              </Box>
              <Box sx={{ width: COLUMN_WIDTHS.discount, textAlign: 'right' }}>
                <InlineTextCell
                  automationId="payment-discount"
                  value={String(payment.discountPct)}
                  editing={isEditing(editKey(payment.id, 'discount'))}
                  onStartEdit={() => startEditing(editKey(payment.id, 'discount'))}
                  onCancel={stopEditing}
                  advanceOnEnter
                  onCommit={(raw, intent) => {
                    const discountPct = parsePctInput(raw);
                    if (discountPct !== null) change(payment.id, { discountPct });
                    advance(payment.id, 'discount', intent);
                  }}
                  align="right"
                  inputWidth={COLUMN_WIDTHS.discount}
                  inputSx={MONO_CELL_SX}
                  display={
                    <Box component="span" sx={MONO_CELL_SX}>
                      {payment.discountPct}%
                    </Box>
                  }
                />
              </Box>
              <Box sx={{ width: COLUMN_WIDTHS.amount, textAlign: 'right' }}>
                <InlineTextCell
                  automationId="payment-amount"
                  value={payment.amount > 0 ? formatAmount(payment.amount) : ''}
                  formatWhileTyping={groupDigitsWhileTyping}
                  placeholder="0"
                  editing={isEditing(editKey(payment.id, 'amount'))}
                  onStartEdit={() => startEditing(editKey(payment.id, 'amount'))}
                  onCancel={stopEditing}
                  advanceOnEnter
                  onCommit={(raw, intent) => {
                    const amount = parseAmountInput(raw);
                    if (amount !== null) change(payment.id, { amount });
                    advance(payment.id, 'amount', intent);
                  }}
                  align="right"
                  inputWidth={COLUMN_WIDTHS.amount}
                  inputSx={{ ...MONO_CELL_SX, fontWeight: 500 }}
                  display={
                    <Box
                      component="span"
                      title={amountTitle(payment)}
                      data-suggested={payment.amount === paymentAmountSuggested(payment)}
                      sx={{ ...MONO_CELL_SX, fontWeight: 500 }}
                    >
                      {formatAmount(payment.amount)}
                    </Box>
                  }
                />
              </Box>
              <Box sx={{ width: COLUMN_WIDTHS.status }}>
                {(() => {
                  const status = paymentStatusOn(payment, today);
                  const style = STATUS_STYLES[status];
                  return (
                    <Box
                      component="button"
                      type="button"
                      data-automation-id="payment-status"
                      data-status={status}
                      title={status === 'overdue' ? t('payments.OverdueTitle') : undefined}
                      onClick={(event: MouseEvent<HTMLElement>) =>
                        setStatusAnchor({ id: payment.id, element: event.currentTarget })
                      }
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.25,
                        px: 0.75,
                        py: 0.25,
                        border: 'var(--border-width-thin) solid var(--border-divider)',
                        borderRadius: 'var(--border-radius-pill)',
                        bgcolor: style.bg,
                        fontSize: 11,
                        fontFamily: 'var(--font-family)',
                        color: style.color,
                        cursor: 'pointer',
                        '&:hover': { borderColor: 'var(--border-subtle)' },
                      }}
                    >
                      {statusLabel(status)}
                      <MaterialSymbol name="expand_more" size={12} />
                    </Box>
                  );
                })()}
              </Box>
              <Box
                component="button"
                type="button"
                data-automation-id="payment-remove"
                aria-label={t('payments.Remove')}
                onClick={() => apply((document) => removePayment(document, payment.id))}
                sx={{
                  width: 16,
                  height: 16,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: 'none',
                  bgcolor: 'transparent',
                  color: 'var(--fg-3)',
                  flex: 'none',
                  p: 0,
                  cursor: 'pointer',
                  '&:hover': { color: 'var(--color-coral-80)' },
                }}
              >
                <MaterialSymbol name="close" size={13} />
              </Box>
            </Box>
          ))}

          <Box
            sx={{
              px: 1,
              py: 1,
              borderTop: 'var(--border-width-thin) solid var(--border-divider)',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              flexWrap: 'wrap',
            }}
          >
            <Box
              component="button"
              type="button"
              data-automation-id="payment-add"
              onClick={add}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                px: 1,
                py: 0.5,
                border: 'var(--border-width-thin) dashed var(--border-divider)',
                borderRadius: 'var(--border-radius-pill)',
                bgcolor: 'transparent',
                fontSize: 11,
                fontFamily: 'var(--font-family)',
                color: 'var(--fg-2)',
                cursor: 'pointer',
                '&:hover': { borderColor: 'var(--border-subtle)', color: 'var(--fg-1)' },
              }}
            >
              <MaterialSymbol name="add" size={14} />
              {t('payments.Add')}
            </Box>
            <Box
              data-automation-id="payment-schedule-total"
              data-state={comparison}
              sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 0.5, fontSize: 11 }}
            >
              {comparison !== 'none' && comparison !== 'ok' && (
                <MaterialSymbol name="info" size={14} color="var(--color-warning-80)" />
              )}
              <Box component="span" sx={{ color: 'var(--fg-3)' }}>
                {t('payments.Scheduled')}
              </Box>
              <Box component="span" sx={{ ...MONO_CELL_SX, fontWeight: 500 }}>
                {formatAmount(scheduled)} {currency}
              </Box>
              <Box component="span" sx={{ color: 'var(--fg-3)' }}>
                {t('payments.OfRevenue', { revenue: formatAmount(revenue) })}
                {comparison === 'under' &&
                  ` · ${t('payments.Unscheduled', { amount: formatAmount(difference), currency })}`}
                {comparison === 'over' &&
                  ` · ${t('payments.Over', { amount: formatAmount(-difference), currency })}`}
              </Box>
            </Box>
          </Box>
        </Box>
      </Collapse>

      <Menu
        anchorEl={unitAnchor?.element ?? null}
        open={unitAnchor !== null}
        onClose={() => setUnitAnchor(null)}
      >
        {PAYMENT_UNITS.map((unit) => {
          const current = payments.find((payment) => payment.id === unitAnchor?.id);
          return (
            <MenuItem
              key={unit}
              selected={current?.unit === unit}
              data-automation-id="payment-unit-option"
              onClick={() => {
                if (unitAnchor) change(unitAnchor.id, { unit });
                setUnitAnchor(null);
              }}
            >
              <MaterialSymbol
                name="check"
                size={14}
                color={current?.unit === unit ? 'var(--fg-2)' : 'transparent'}
              />
              <Box component="span" sx={{ ml: 1 }}>
                {unitLabel(unit)}
              </Box>
            </MenuItem>
          );
        })}
      </Menu>

      <Menu
        anchorEl={statusAnchor?.element ?? null}
        open={statusAnchor !== null}
        onClose={() => setStatusAnchor(null)}
      >
        {PAYMENT_STATUSES.map((status: PaymentStatus) => {
          const current = payments.find((payment) => payment.id === statusAnchor?.id);
          return (
            <MenuItem
              key={status}
              selected={current?.status === status}
              data-automation-id="payment-status-option"
              onClick={() => {
                if (statusAnchor) change(statusAnchor.id, { status });
                setStatusAnchor(null);
              }}
            >
              <MaterialSymbol
                name="check"
                size={14}
                color={current?.status === status ? 'var(--fg-2)' : 'transparent'}
              />
              <Box component="span" sx={{ ml: 1 }}>
                {statusLabel(status)}
              </Box>
            </MenuItem>
          );
        })}
      </Menu>
    </Box>
  );
};
